import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Calendar, Package, Sun, Building, Laptop, Monitor, Phone,
  PartyPopper, Star, Gift, Clock,
} from 'lucide-react';
import api from '../lib/api';

const holidayTypeConfig = {
  national: { label: 'National', color: 'bg-red-100 text-red-800', icon: Star },
  regional: { label: 'Regional', color: 'bg-blue-100 text-blue-800', icon: Sun },
  company: { label: 'Company', color: 'bg-green-100 text-green-800', icon: Building },
  optional: { label: 'Optional', color: 'bg-yellow-100 text-yellow-800', icon: Gift },
};

const assetCategoryIcons = {
  laptop: Laptop,
  monitor: Monitor,
  phone: Phone,
  default: Package,
};

export default function GuestPortalPage() {
  const { user } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('holidays');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [holidaysRes, assetsRes] = await Promise.all([
        api.get('/holidays'),
        api.get('/assets/available'),
      ]);
      setHolidays(holidaysRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      console.error('Error fetching guest data:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const upcomingHolidays = holidays
    .filter(h => new Date(h.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5);

  const getAssetIcon = (category) => {
    const Icon = assetCategoryIcons[category?.toLowerCase()] || assetCategoryIcons.default;
    return Icon;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">
            Welcome{user?.name ? `, ${user.name}` : ''}
          </h1>
          <p className="text-muted-foreground">
            View company holidays and available assets
          </p>
        </div>
        <Badge variant="outline" className="text-sm">
          <Clock className="w-3 h-3 mr-1" />
          Guest Access
        </Badge>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-red-100">
                <Calendar className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{holidays.length}</p>
                <p className="text-sm text-muted-foreground">Total Holidays ({currentYear})</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-100">
                <PartyPopper className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{upcomingHolidays.length}</p>
                <p className="text-sm text-muted-foreground">Upcoming Holidays</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-100">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{assets.length}</p>
                <p className="text-sm text-muted-foreground">Available Assets</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="holidays" className="gap-2">
            <Calendar className="w-4 h-4" />
            Holidays
          </TabsTrigger>
          <TabsTrigger value="assets" className="gap-2">
            <Package className="w-4 h-4" />
            Available Assets
          </TabsTrigger>
        </TabsList>

        {/* Holidays Tab */}
        <TabsContent value="holidays">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Upcoming Holidays */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PartyPopper className="w-5 h-5 text-primary" />
                  Upcoming
                </CardTitle>
                <CardDescription>Next holidays coming up</CardDescription>
              </CardHeader>
              <CardContent>
                {upcomingHolidays.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    No upcoming holidays
                  </p>
                ) : (
                  <div className="space-y-3">
                    {upcomingHolidays.map((holiday) => {
                      const config = holidayTypeConfig[holiday.type] || holidayTypeConfig.company;
                      const TypeIcon = config.icon;
                      const holidayDate = new Date(holiday.date);
                      const daysUntil = Math.ceil((holidayDate - new Date()) / (1000 * 60 * 60 * 24));

                      return (
                        <div
                          key={holiday.id}
                          className="p-3 rounded-lg border bg-muted/20 hover:bg-muted/40 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <TypeIcon className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{holiday.name}</span>
                            </div>
                            <Badge className={config.color} variant="secondary">
                              {config.label}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm text-muted-foreground">
                              {formatDate(holiday.date)}
                            </span>
                            <span className="text-xs font-medium text-primary">
                              {daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* All Holidays Table */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Holiday Calendar {currentYear}
                </CardTitle>
                <CardDescription>Complete list of company holidays</CardDescription>
              </CardHeader>
              <CardContent>
                {holidays.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No holidays configured yet</p>
                  </div>
                ) : (
                  <div className="max-h-[400px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Day</TableHead>
                          <TableHead>Holiday</TableHead>
                          <TableHead>Type</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {holidays
                          .sort((a, b) => new Date(a.date) - new Date(b.date))
                          .map((holiday) => {
                            const config = holidayTypeConfig[holiday.type] || holidayTypeConfig.company;
                            const date = new Date(holiday.date);
                            const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                            const isPast = date < new Date();

                            return (
                              <TableRow key={holiday.id} className={isPast ? 'opacity-50' : ''}>
                                <TableCell className="font-medium">
                                  {formatDate(holiday.date)}
                                </TableCell>
                                <TableCell>{dayName}</TableCell>
                                <TableCell>{holiday.name}</TableCell>
                                <TableCell>
                                  <Badge className={config.color} variant="secondary">
                                    {config.label}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Assets Tab */}
        <TabsContent value="assets">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Available Assets
              </CardTitle>
              <CardDescription>Assets currently available for allocation</CardDescription>
            </CardHeader>
            <CardContent>
              {assets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No assets currently available</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {assets.map((asset) => {
                    const AssetIcon = getAssetIcon(asset.category);
                    return (
                      <div
                        key={asset.id}
                        className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <AssetIcon className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium truncate">{asset.name}</h4>
                            <p className="text-sm text-muted-foreground">{asset.category}</p>
                          </div>
                        </div>
                        <div className="mt-3 space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Tag ID:</span>
                            <span className="font-mono">{asset.tag_id}</span>
                          </div>
                          {asset.location && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Location:</span>
                              <span>{asset.location}</span>
                            </div>
                          )}
                        </div>
                        <Badge className="mt-3 bg-green-100 text-green-800">
                          Available
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
